import L from "leaflet";
import "leaflet-contextmenu";
import { v4 as uuidv4 } from "uuid";
import store from "redux/store";

// utilities
import { getAirportById } from "hooks/useAirport";
import calcDistance from "common/calcDistance";
import getMap from "common/getMap";

// classes
import Aircraft from "./aircraft";

//assets
import greenPin from "assets/images/map/pins/pinGreen.svg";
import darkGreenPin from "assets/images/map/pins/pinDarkGreen.svg";
import orangePin from "assets/images/map/pins/pinOrange.svg";
import { getModelData } from "hooks/useModelData";

class Route {
	id;
	from;
	to;
	polylines;
	aircraft;
	options;
	distance;

	reversed = false;
	constructor(from, to, options = {}, obj) {
		if (obj) {
			Object.assign(this, obj);
		} else {
			this.id = uuidv4();
			this.from = from;
			this.to = to;
			this.polylines = [null, null];
			this.aircraft = [];
			this.options = options;
			store.dispatch({ type: "ROUTES_ADD", state: this });
		}

		if (!this.distance) {
			const airportList = store.getState().airports;
			const { latitude_deg: lat1, longitude_deg: lng1 } = getAirportById(airportList, this.from, null);
			const { latitude_deg: lat2, longitude_deg: lng2 } = getAirportById(airportList, this.to, null);

			if (!lat1 || !lat2 || !lng1 || !lng2) {
				console.log(this.id, this.from, this.to);
				throw new Error("One of 'lat1', 'lat2', 'lng1' or 'lng2' is not defined");
			}

			this.distance = calcDistance(lat1, lng1, lat2, lng2);
		}

		!this.options && (this.options = options);
		if (this.aircraft.length > 0) this.drawRoute();

		document.addEventListener("removeRouteFromMap", ({ detail }) => {
			if (detail.id !== this.id) return;
			this.removeFromMap();
		});

		store.subscribe(() => {
			const state = store.getState();
			const address = state.sideWindow.currentAddress.split("~");
			if (!this.destinationPin) return;
			if (address[0] !== "airport") {
				this.pinClicked = false;
				return;
			}

			const newClickedPin = +address[1];

			// showing
			if (newClickedPin === this.to && !this.pinClicked) {
				this.pinClicked = true;
			}

			// hiding
			if (newClickedPin !== this.to && this.pinClicked) {
				this.pinClicked = false;
			}
		});

		return this;
	}

	get drawn() {
		return this.polylines != null && !this.polylines.includes(null);
	}

	#pinClicked;
	get pinClicked() {
		return this.#pinClicked;
	}

	set pinClicked(value) {
		this.#pinClicked = value;

		if (value) {
			this.destinationPin?.setIcon(
				L.icon({
					iconUrl: darkGreenPin,
					iconSize: [26, 26],
					iconAnchor: [13, 26]
				})
			);
		} else {
			this.destinationPin?.setIcon(
				L.icon({
					iconUrl: greenPin,
					iconSize: [26, 26],
					iconAnchor: [13, 26]
				})
			);
		}
	}

	/**
	 * Removes an aircraft from the route.
	 * @param {number} aircraftIdent - The identifier of the aircraft to be removed.
	 */
	removeAircraft(aircraftIdent) {
		const routeWithoutCurrAircraft = this.aircraft.filter(aircraftId => aircraftId !== aircraftIdent); //! this is only an aircraft array not whole route object
		this.aircraft = routeWithoutCurrAircraft;
		this.unbindContextMenu();

		if (routeWithoutCurrAircraft.length > 0) {
			routeWithoutCurrAircraft.length > 1 && this.bindContextMenu();
			store.dispatch({ type: "ROUTE_UPDATE", id: this.id, state: this });
			return;
		}

		this.removeFromMap();
		store.dispatch({ type: "ROUTE_REMOVE", id: this.id });
	}

	/**
	 * @param {number} aircraftIdent
	 * @returns {Route} this
	 */
	async addAircraft(aircraftIdent) {
		!this.aircraft.includes(aircraftIdent) && this.aircraft.push(aircraftIdent);

		if (!this.drawn) {
			await this.drawRoute();
			this.unbindContextMenu();
			this.bindContextMenu();
		}

		store.dispatch({ type: "ROUTE_UPDATE", id: this.id, state: this });
		return this;
	}

	bindContextMenu() {
		if (this.aircraft.length <= 1) return;

		const items = this.polylines;
		if (this.destinationPin) items.push(this.destinationPin);

		items.forEach(item => {
			if (!item) return true;

			const contextMenuItems = this.aircraft.map((a, i) => {
				const aircraft = Aircraft.get(a);
				const plane = getModelData(aircraft.id);

				return {
					text: `${plane.name}   ${aircraft.registration}`,
					index: i + 1,
					iconCls: "fas fa-plane",
					callback: () => {
						document.dispatchEvent(new CustomEvent("goToAircraftOnMap", { detail: { ident: a } }));
					}
				};
			});

			const airports = store.getState().airports;
			const origin = getAirportById(airports, this.from);
			const dest = getAirportById(airports, this.to);
			contextMenuItems.unshift({
				text: `${origin?.iata_code || origin?.ident} <i className="fas fa-arrow-right"></i> ${dest?.iata_code ||
					dest?.ident}`,
				index: 0,
				hideOnSelect: false
			});

			const contextMenu = item.bindContextMenu({
				contextmenu: true,
				contextmenuInheritItems: false,
				contextmenuItems: contextMenuItems
			});

			item.addEventListener("click", e => {
				var itemOptions, data, pt, i, l;
				if (contextMenu._map.contextmenu) {
					data = L.extend({ relatedTarget: contextMenu }, e);

					pt = e.latlng;

					if (!contextMenu.options.contextmenuInheritItems) {
						window.map.contextmenu.hideAllItems();
					}

					for (i = 0, l = contextMenu.options.contextmenuItems.length; i < l; i++) {
						itemOptions = contextMenu.options.contextmenuItems[i];
						contextMenu._items.push(window.map.contextmenu.insertItem(itemOptions, itemOptions.index));
					}

					window.map.once("contextmenu.hide", contextMenu._hideContextMenu, contextMenu);
					window.map.contextmenu.showAt(pt, data);
				}
			});
		});
	}

	unbindContextMenu() {
		if (this.polylines == null) return;
		this.polylines.forEach(line => {
			if (!line) return true;
			line.unbindContextMenu();
		});
	}

	// asynchronous methods

	/**
	 * @return Promise<Route> A Promise resolving to this
	 */
	async drawRoute() {
		if (!this.id || !this.from || !this.to) return;

		const map = await getMap();
		const airportList = store.getState().airports;
		const mapStyle = store.getState().map.type;
		let { latitude_deg: lat1, longitude_deg: lng1 } = getAirportById(airportList, this.from, null);
		let { latitude_deg: lat2, longitude_deg: lng2 } = getAirportById(airportList, this.to, null);

		if (!lat1 || !lat2 || !lng1 || !lng2) {
			console.log(this.id, this.from, this.to);
			throw new Error("One of 'lat1', 'lat2', 'lng1' or 'lng2' is not defined");
		}

		const { originPin = false, destinationPin = true, type = "normal" } = this.options || {};

		const color =
			type === "delivery"
				? "rgba(146, 52, 235, .5)"
				: +mapStyle === 4 || +mapStyle === 3
				? "rgba(71, 179, 255, .8)"
				: "rgba(21, 51, 99, .8)";
		const pathOptions = {
			color: color,
			weight: 2.5,
			vertices: Math.max(Math.floor(this.distance / 5), 20) // 1 vertex per 5 km but no less than 20,
		};

		if (lng2 < lng1) {
			this.reversed = true;
			[[lat2, lng2], [lat1, lng1]] = [[lat1, lng1], [lat2, lng2]];
		}

		window.routesLayer ??= L.layerGroup().addTo(map);
		const line1 = L.Polyline.Arc([lat1, lng1], [lat2, lng2], pathOptions).addTo(window.routesLayer);
		const line2 = L.Polyline.Arc([lat1, lng1 - 360], [lat2, lng2 - 360], pathOptions).addTo(window.routesLayer);
		this.polylines = [line1, line2];

		if (destinationPin) {
			const destinationPinMarker = L.marker(this.reversed ? [lat1, lng1] : [lat2, lng2], {
				icon: L.icon({
					iconUrl: greenPin,
					iconSize: [26, 26],
					iconAnchor: [13, 26]
				}),
				zIndexOffset: 1000
			}).addTo(map);

			destinationPinMarker.on("click", () => {
				const currentlyShown = +store.getState().sideWindow.currentAddress.split("~")[1];
				if (currentlyShown === this.to) {
					store.dispatch({ type: "SET_SIDE_WINDOW_ADDRESS", state: "" });
				} else {
					store.dispatch({ type: "SET_SIDE_WINDOW_ADDRESS", state: `airport~${this.to}` });
				}
			});

			this.destinationPin = destinationPinMarker;
		}

		if (originPin) {
			const originPinMarker = L.marker(!this.reversed ? [lat1, lng1] : [lat2, lng2], {
				icon: L.icon({
					iconUrl: orangePin,
					iconSize: [26, 26],
					iconAnchor: [13, 26]
				}),
				zIndexOffset: 1000
			}).addTo(map);
			this.originPin = originPinMarker;
		}

		store.dispatch({ type: "ROUTE_UPDATE", id: this.id, state: this });

		this.bindContextMenu();
		this.polylines.forEach(line => {
			line.addEventListener("click", () => {
				if (this.aircraft.length !== 1) return;
				document.dispatchEvent(new CustomEvent("goToAircraftOnMap", { detail: { ident: this.aircraft[0] } }));
			});
		});

		document.addEventListener("map_change", () => {
			const mapStyle = store.getState().map.type;

			this.polylines.forEach(line => {
				if (typeof line.setStyle !== "function") return true;
				line.setStyle({
					color:
						this.options.type === "delivery"
							? "rgba(146, 52, 235, .5)"
							: +mapStyle === 4 || +mapStyle === 3
							? "rgba(71, 179, 255, .8)"
							: "rgba(21, 51, 99, .8)"
				});
			});
		});

		return this;
	}

	removeFromMap() {
		// console.log(Object.values(window.routesLayer._layers).length);
		// console.trace();
		console.log("remove route from map");

		// const map = await getMap();
		this.polylines.forEach(l => l?.remove());

		if (this.destinationPin) {
			this.destinationPin.remove();
			this.destinationPin = null;
		}

		if (this.originPin) {
			this.originPin.remove();
			this.originPin = null;
		}

		store.dispatch({ type: "ROUTE_UPDATE", id: this.id, state: this });
	}

	/**
	 * A method that returns the LatLngs of route's polylines.
	 * @return {Array<LatLng>} The LatLngs of route's polylines.
	 * @async
	 */
	async getLatLngs() {
		if (!this.polylines || this.polylines.includes(null)) await this.drawRoute();
		const points = [[...this.polylines[0].getLatLngs()], [...this.polylines[1].getLatLngs()]];
		this.reversed && points[0].reverse();
		this.reversed && points[1].reverse();
		return points.map(a => a.map(p => L.latLng(p.lat, p.lng)));
	}

	// static methods

	/**
	 * Crates an instance of a route, but if same route exists returns it instead.
	 * @param {Number} from
	 * @param {Number} to
	 * @param {Object} options
	 * @param {Object} obj Provide if re-creating a route
	 * @return {Route}
	 */
	static new(from, to, options, obj) {
		const exists = Route.exists(from, to);
		return exists ? exists : new Route(from, to, options, obj);
	}

	/**
	 * A static method used to check if a route with the same 'from' and 'to' airports already exists.
	 * @param {Number} from - The identifier of the departure airport.
	 * @param {Number} to - The identifier of the destination airport.
	 * @return {Route | undefined} - Returns the existing route if found, otherwise returns undefined.
	 */
	static exists(from, to) {
		return Route.getAll().find(r => r.from === from && r.to === to);
	}

	/**
	 * Returns all routes saved in redux
	 */
	static getAll() {
		return store.getState().routes;
	}

	/**
	 * A method used to get a Route based on its identifier
	 * @param {String} id The identifier of the aircraft.
	 * @return {Route | undefined} Returns the route or undefined if not found.
	 */
	static get(id) {
		return Route.getAll().find(r => r.id === id);
	}

	static async deleteAll() {
		//Remove all routes from map
		store.getState().routes.forEach(r => r.removeFromMap());

		// Remove all routes
		store.dispatch({ type: "ROUTES_SET", state: [] });

		//Remove route from every aircraft
		const newFleet = [...store.getState().fleet.values()].map(aircraft => {
			aircraft.route = null;
			return aircraft;
		});

		store.dispatch({ type: "FLEET_SET", state: newFleet });
		Aircraft.saveFleet(newFleet);
	}

	// object conversions
	/**
	 * A method used to convert a Route instance into an object. Also outputs private properties!
	 * @return {Object} A Route instance converted to an object
	 */
	toObject() {
		const keys = Object.getOwnPropertyNames(Object.getPrototypeOf(this));

		let instanceAsObject = keys.reduce((classAsObj, key) => {
			classAsObj[key] = this[key];
			return classAsObj;
		}, {});

		instanceAsObject = Object.keys(this).reduce((classAsObj, key) => {
			classAsObj[key] = this[key];
			return classAsObj;
		}, instanceAsObject);

		for (let key in instanceAsObject) {
			if (typeof instanceAsObject[key] === "function") delete instanceAsObject[key];
			if (key[0] === "_") delete instanceAsObject[key];
		}

		delete instanceAsObject.toObject;
		delete instanceAsObject.constructor;
		return instanceAsObject;
	}
}

export default Route;
